import { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { X, HelpCircle, BookOpen, Lightbulb, ChevronRight, UserCheck } from 'lucide-react';
import { helpContent } from '../data/helpContent';
import { api } from '../services/api';

const ROLE_BADGE = {
  admin:   { label: 'Admin',   cls: 'bg-purple-500/20 text-purple-300' },
  manager: { label: 'Gerente', cls: 'bg-blue-500/20 text-blue-300' },
  esn:     { label: 'ESN',     cls: 'bg-emerald-500/20 text-emerald-300' },
};

export default function HelpPanel({ isOpen, onClose }) {
  const { pathname } = useLocation();
  const help = helpContent[pathname];
  const [appVersion, setAppVersion] = useState('2.0');
  useEffect(() => {
    api.getVersion().then(v => setAppVersion(v?.version || '2.0')).catch(() => null);
  }, []);

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={`
          fixed top-0 right-0 h-full w-[380px] max-w-[94vw] z-50 flex flex-col
          bg-white shadow-2xl border-l border-gray-200
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        {/* ── Header ── */}
        <div className="bg-[#002233] px-5 pt-4 pb-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <HelpCircle size={15} className="text-[#00DBFF]" />
              <span className="text-white/60 text-[11px] font-semibold tracking-wide uppercase">
                Central de Ajuda
              </span>
            </div>
            <button
              onClick={onClose}
              className="text-white/40 hover:text-white transition-colors p-1 rounded"
              title="Fechar ajuda"
            >
              <X size={16} />
            </button>
          </div>

          {help ? (
            <>
              <h2 className="text-white font-bold text-[16px] leading-tight">{help.title}</h2>
              {help.roles?.length > 0 && (
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  <UserCheck size={11} className="text-white/30" />
                  {help.roles.map(r => (
                    <span
                      key={r}
                      className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${ROLE_BADGE[r]?.cls}`}
                    >
                      {ROLE_BADGE[r]?.label}
                    </span>
                  ))}
                </div>
              )}
            </>
          ) : (
            <h2 className="text-white/50 font-medium text-sm">
              Sem ajuda para esta tela
            </h2>
          )}
        </div>

        {/* ── Content ── */}
        <div className="flex-1 overflow-y-auto">
          {help ? (
            <div className="p-5 space-y-5">

              {/* Description */}
              <p className="text-[13px] text-gray-600 leading-relaxed border-l-2 border-[#00DBFF]/40 pl-3">
                {help.description}
              </p>

              {/* How-to sections */}
              {help.sections?.length > 0 && (
                <div>
                  <h3 className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
                    <BookOpen size={11} />
                    Como usar
                  </h3>
                  <div className="space-y-2.5">
                    {help.sections.map((sec, i) => (
                      <div key={i} className="bg-gray-50 rounded-xl p-3.5 border border-gray-100">
                        <div className="flex items-start gap-2.5 mb-1.5">
                          <span className="w-5 h-5 rounded-full bg-[#002233] text-[#00DBFF] text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-px">
                            {i + 1}
                          </span>
                          <h4 className="text-[13px] font-semibold text-gray-800">{sec.title}</h4>
                        </div>
                        <p className="text-[12px] text-gray-500 leading-relaxed pl-[28px]">
                          {sec.content}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tips */}
              {help.tips?.length > 0 && (
                <div>
                  <h3 className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
                    <Lightbulb size={11} />
                    Dicas e boas práticas
                  </h3>
                  <ul className="space-y-2">
                    {help.tips.map((tip, i) => (
                      <li key={i} className="flex items-start gap-2 text-[12px] text-gray-600">
                        <ChevronRight size={13} className="text-[#00DBFF] mt-px flex-shrink-0" />
                        <span className="leading-relaxed">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center p-10 gap-4">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                <HelpCircle size={28} className="text-gray-300" />
              </div>
              <div>
                <p className="text-gray-400 text-sm font-medium">Documentação não disponível</p>
                <p className="text-gray-300 text-xs mt-1">para esta página</p>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="border-t border-gray-100 px-5 py-3 flex-shrink-0 bg-gray-50/50">
          <p className="text-[10px] text-gray-300 text-center tracking-wide">
            CRM TOTVS Moda — Manual de Operação v{appVersion}
            <span className="mx-2 text-gray-200">|</span>
            TOTVS Oeste
          </p>
        </div>
      </div>
    </>
  );
}
